import React, { useState } from "react";
import { Question, Buildings, Briefcase, Brain, ChartLineUp, Calendar, Key, UsersThree, ShieldCheck } from "@phosphor-icons/react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../lib/auth";

// Help topics per role.

const TOPICS = {
  // Bedrijfsadmin (work coach with extra rights within a single company)
  ba_overview: {
    title: "Wat je als bedrijfsadmin kunt",
    icon: ShieldCheck,
    body: [
      "Als bedrijfsadmin heb je dezelfde rechten als een reguliere work coach, maar daarnaast kun je ook alle work coaches en coachees binnen jouw bedrijf beheren. Je kunt nieuwe work coaches toevoegen, hun gegevens bewerken en coachees toewijzen.",
      "Het enige wat je NIET kunt: het bedrijf zelf verwijderen. Dat recht ligt bij de admin van het platform.",
    ],
  },
  ba_company: {
    title: "Bedrijfsgegevens beheren",
    icon: Buildings,
    body: [
      "Open 'Mijn bedrijf' in het menu om de bedrijfsgegevens te zien. Je kunt naam en beschrijving aanpassen via de knop 'Bedrijf'. Het overzicht laat alle work coaches binnen je bedrijf zien.",
    ],
  },
  ba_coaches: {
    title: "Work coaches beheren",
    icon: Briefcase,
    body: [
      "Op de bedrijfspagina kun je nieuwe work coaches toevoegen met de knop 'Work coach toevoegen'. Vul naam, e-mail en wachtwoord in. Je kunt bij creatie of later via 'Bewerken' iemand bedrijfsadmin maken — die persoon krijgt dan dezelfde rechten als jij.",
      "Je kunt jezelf niet verwijderen. Een work coach verwijderen is een cascading delete — alle gekoppelde coachees gaan ook weg.",
    ],
  },
  ba_coachees: {
    title: "Coachees beheren",
    icon: UsersThree,
    body: [
      "Op 'Coachees van het bedrijf' zie je alle coachees in het hele bedrijf. Met het filter 'Alle work coaches' kun je inzoomen op één coach. Bij het toevoegen van een nieuwe coachee kies je aan welke work coach hij/zij gekoppeld wordt.",
      "Bij creatie kun je het breintype direct invullen als je dat al weet — dan wordt er geen online test beschikbaar gemaakt. Laat het veld leeg om de coachee de online breintype-test te laten maken.",
    ],
  },

  // Work coach
  jc_overview: {
    title: "Welkom bij Werk Dat Past Online",
    icon: Briefcase,
    body: [
      "Werk Dat Past Online is je platform om coachees te begeleiden op basis van hun individuele breintype. Je beheert je eigen coachees, laat ze de online breintype-test maken en bespreekt hun uitslag via een persoonlijk gesprek.",
    ],
  },
  jc_coachees: {
    title: "Coachees aanmaken en beheren",
    icon: UsersThree,
    body: [
      "Klik op 'Mijn coachees' in het menu. Met 'Coachee toevoegen' maak je een nieuwe coachee aan. Vul naam, e-mail en wachtwoord in — daarmee logt de coachee in om de online test te maken of zijn breintype te bekijken.",
      "Vul het breintype direct in als je dat al kent uit eerdere gesprekken — er wordt dan geen online test beschikbaar voor de coachee. Laat het veld leeg om de online test open te zetten.",
    ],
  },
  jc_brain_type: {
    title: "Hoe het breintype wordt bepaald",
    icon: ChartLineUp,
    body: [
      "Er zijn twee manieren om het breintype van een coachee vast te leggen:",
      "1. Online breintype-test — 90 stellingen + 5 keuzevragen. De Bayesiaanse berekening genereert waarschijnlijkheden per breintype en kiest het meest waarschijnlijke.",
      "2. Handmatig — als je het breintype al weet uit een gesprek, vul je het direct in bij het aanmaken van de coachee.",
      "Op het 'Breintype'-tabblad van een coachee zie je de uitleg, de afbeelding en de bijbehorende patronen.",
    ],
  },
  jc_calendly: {
    title: "Calendly koppelen",
    icon: Calendar,
    body: [
      "Ga naar 'Instellingen' en plak je Calendly inline-widget snippet in het veld. Zodra opgeslagen wordt de boekingswidget getoond op de breintype-pagina van elke coachee en direct na het voltooien van de online test.",
      "De widget verschijnt niet in jouw eigen view — alleen bij de coachee.",
    ],
  },

  // Coachee
  c_overview: {
    title: "Welkom",
    icon: Brain,
    body: [
      "Welkom bij Werk Dat Past Online! Via dit platform krijg je inzicht in je eigen breintype op basis van het Neurogram framework. Je doet een korte test van circa 15-20 minuten en je work coach bespreekt vervolgens de uitslag persoonlijk met je.",
    ],
  },
  c_survey: {
    title: "De breintype-test maken",
    icon: ChartLineUp,
    body: [
      "Vanaf 'Mijn breintype' kun je de breintype-test starten als die voor jou beschikbaar is. Je krijgt 90 stellingen plus 5 keuzevragen — antwoord steeds zo eerlijk mogelijk.",
      "De volgorde van de vragen is willekeurig — laat je niet afleiden door ogenschijnlijk soortgelijke vragen kort na elkaar.",
      "Na het verzenden zie je direct je voorlopige uitslag met de waarschijnlijkheid per breintype.",
    ],
  },
  c_brain_type: {
    title: "Je breintype bekijken",
    icon: Brain,
    body: [
      "Op 'Mijn breintype' zie je je breintype met een uitgebreide uitleg, een illustratie en typerende eigenschappen.",
      "Helemaal onderaan staat een Calendly-blok van je work coach — daar kun je rechtstreeks een gesprek inplannen.",
    ],
  },
  c_password: {
    title: "Wachtwoord wijzigen",
    icon: Key,
    body: [
      "Klik op 'Wachtwoord wijzigen' in het menu. Voer je huidige wachtwoord in plus je gewenste nieuwe wachtwoord. Na opslaan kun je gewoon doorgaan — uitloggen is niet nodig.",
    ],
  },
};

function topicsFor(user) {
  if (!user) return [];
  if (user.role === "job_coach") {
    if (user.is_bedrijfsadmin) {
      return ["ba_overview", "ba_company", "ba_coaches", "ba_coachees", "jc_brain_type", "jc_calendly"];
    }
    return ["jc_overview", "jc_coachees", "jc_brain_type", "jc_calendly"];
  }
  if (user.role === "coachee") {
    return ["c_overview", "c_survey", "c_brain_type", "c_password"];
  }
  return [];
}

function roleLabel(user) {
  if (!user) return "";
  if (user.role === "job_coach") return user.is_bedrijfsadmin ? "Bedrijfsadmin" : "Work coach";
  if (user.role === "coachee") return "Coachee";
  return user.role;
}

export default function HelpPage() {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(null);
  const topics = topicsFor(user);
  const currentId = activeId || topics[0];
  const current = currentId ? TOPICS[currentId] : null;

  if (user?.role === "admin") {
    return (
      <AppLayout title="Help" breadcrumbs={[{ label: "Help" }]}>
        <div className="neuro-card p-8 max-w-2xl">
          <h2 className="font-display text-xl font-bold tracking-tight">Geen help-pagina voor admins</h2>
          <p className="text-sm text-zinc-600 mt-2">
            De admin-rol is bedoeld voor platform-beheer. Documentatie staat in interne handleidingen.
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Help" breadcrumbs={[{ label: "Help" }]}>
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 max-w-5xl" data-testid="help-page">
        <aside className="neuro-card p-4 self-start sticky top-24" data-testid="help-sidebar">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
            Help voor {roleLabel(user)}
          </div>
          <ul className="space-y-1">
            {topics.map((id) => {
              const t = TOPICS[id];
              const Icon = t.icon || Question;
              const active = id === currentId;
              return (
                <li key={id}>
                  <button
                    onClick={() => setActiveId(id)}
                    data-testid={`help-topic-${id}`}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      active
                        ? "bg-zinc-900 text-white"
                        : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    <Icon size={16} className="mt-0.5 shrink-0" />
                    <span>{t.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <article className="neuro-card p-8 md:p-10" data-testid="help-content">
          {current ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                {React.createElement(current.icon || Question, { size: 24, weight: "duotone", className: "text-[#1E88E5]" })}
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="help-content-title">
                  {current.title}
                </h2>
              </div>
              <div className="space-y-4 text-[15px] leading-relaxed text-zinc-800">
                {current.body.map((para) => (
                  <p key={para.slice(0, 64)} className="whitespace-pre-line">{para}</p>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-zinc-500">Geen onderwerpen beschikbaar voor je rol.</p>
          )}
        </article>
      </div>
    </AppLayout>
  );
}
